/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, {
  Dispatch,
  HTMLAttributes,
  FC,
  SetStateAction,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Button } from '..';
import moment, { Moment } from 'moment';
import { DatePickerHeader } from './DatePickerHeader';
import { buildDatePicker, styleDates } from './helpers';
import { getPrevMonth, getNextMonth } from './util/headerFunctions';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DatePickerProps extends HTMLAttributes<HTMLDivElement> {
  currentDate?: Moment | null;
  handleOnChange?: Dispatch<SetStateAction<Moment | null>> | undefined;
  calendarMoment?: Moment | undefined;
  setCalendarMoment?: Dispatch<SetStateAction<Moment | undefined>>;
  setDisplayedDate: Dispatch<SetStateAction<string>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  className?: string;
  selectableDates?: 'all' | 'future' | 'current/future' | 'current/past';
  focusedDay: Moment | null;
  setFocusedDay: Dispatch<SetStateAction<Moment | null>>;
  hideFooter?: boolean;
}

export const DatePickerBody: FC<DatePickerProps> = ({
  className,
  setDisplayedDate,
  currentDate,
  handleOnChange,
  calendarMoment,
  setCalendarMoment,
  open,
  setOpen,
  selectableDates,
  focusedDay,
  setFocusedDay,
  hideFooter,
  ...rest
}): JSX.Element => {
  const [calendar, setCalendar] = useState<Moment[][]>([]);
  const [cellIsFocused, setCellIsFocused] = useState<boolean>(false);
  const calendarCellRef = useRef<any>(null);

  const handleCancelRegular = () => {
    handleOnChange && handleOnChange(null);
    setDisplayedDate('');
    setOpen(false);
  };

  useEffect(() => {
    if (currentDate) {
      setFocusedDay(currentDate);
    } else {
      setFocusedDay(moment());
    }
  }, []);

  useEffect(() => {
    if (!calendarMoment && setCalendarMoment) {
      setCalendarMoment(moment());
    }
    if (calendarMoment) {
      setCalendar(buildDatePicker(moment(calendarMoment)));
    }
  }, [calendarMoment]);

  // Functions for keyboard inputs

  const handleDateSelection = (date: Moment) => {
    if (selectableDates === 'all') {
      handleOnChange && handleOnChange(date);
    } else if (selectableDates === 'future') {
      if (date.isAfter(moment(), 'day')) {
        handleOnChange && handleOnChange(date);
      }
    } else if (selectableDates === 'current/future') {
      if (date.isSameOrAfter(moment(), 'day')) {
        handleOnChange && handleOnChange(date);
      }
    } else if (selectableDates === 'current/past') {
      if (date.isSameOrBefore(moment(), 'day')) {
        handleOnChange && handleOnChange(date);
      }
    }
  };

  const handleDateSelectionKB = (date: Moment) => {
    setFocusedDay(date);
  };
  const setPreviousDay = (day: Moment) => {
    const previousDay = day.subtract(1, 'days');
    handleDateSelectionKB(previousDay);
  };
  const setNextDay = (day: Moment) => {
    const nextDay = day.add(1, 'days');
    handleDateSelectionKB(nextDay);
  };
  const setPreviousWeek = (day: Moment) => {
    const previousWeek = day.subtract(1, 'weeks');
    handleDateSelectionKB(previousWeek);
  };
  const setNextWeek = (day: Moment) => {
    const nextWeek = day.add(1, 'weeks');
    handleDateSelectionKB(nextWeek);
  };

  // e.preventDefault() placed on certain keys due to the need for preventing particular default behaviors while other default behaviors for keys like "tab" are necessary.
  const handleCalendarKeyPress = (e: any, day: Moment) => {
    const key = e.key;
    const momentDay = moment(day); // day is wrapped in moment, styling functionalities won't work if moment wrapper is removed.
    switch (key) {
      case 'Enter': // Enter
        e.preventDefault();
        handleDateSelection(momentDay);
        return;
      case 'Escape': // Esc
        setOpen(false);
        return;
      case 'Esc': // Esc
        setOpen(false);
        return;
      case ' ': // Space
        e.preventDefault();
        handleDateSelection(momentDay);
        return;
      case 'Spacebar': // Space
        e.preventDefault();
        handleDateSelection(momentDay);
        return;
      case 'ArrowLeft': // left
        e.preventDefault();
        setPreviousDay(momentDay);
        return;
      case 'ArrowUp': // up
        e.preventDefault();
        setPreviousWeek(momentDay);
        return;
      case 'ArrowRight': // right
        e.preventDefault();
        setNextDay(momentDay);
        return;
      case 'ArrowDown': // down
        e.preventDefault();
        setNextWeek(momentDay);
        return;
    }
  };

  // makes sure the calendar changes months when you go from one month to another while navigating through the calendar cells
  useEffect(() => {
    if (open) {
      if (focusedDay?.isAfter(calendarMoment, 'month')) {
        if (calendarMoment && setCalendarMoment) {
          setCalendarMoment(getNextMonth(calendarMoment));
        }
      }
      if (focusedDay?.isBefore(calendarMoment, 'month')) {
        if (calendarMoment && setCalendarMoment) {
          setCalendarMoment(getPrevMonth(calendarMoment));
        }
      }
    }
  }, [focusedDay]);

  // fires on every re-render, no dependency array needed
  useEffect(() => {
    if (open && calendarCellRef.current && cellIsFocused)
      calendarCellRef.current.focus();
  });

  // built for when the user hits an arrow button to navigate to a new month. KB will focus the 1st date of the month when focused
  useEffect(() => {
    const calendarMonth = calendarMoment?.month();
    const calendarYear = calendarMoment?.year();
    if (
      calendarMonth &&
      calendarYear &&
      calendarMonth !== focusedDay?.month()
    ) {
      if (currentDate && calendarMonth === currentDate.month()) {
        setFocusedDay(currentDate);
      } else {
        const newFocusedDate = moment()
          .year(calendarYear)
          .month(calendarMonth)
          .date(1);
        setFocusedDay(newFocusedDate);
      }
    }
  }, [calendarMoment]);

  return (
    <div className={`${className || ''}`}>
      <div {...rest} className="p-5 pb-3">
        <DatePickerHeader
          calendarMoment={calendarMoment}
          setCalendarMoment={setCalendarMoment}
          setFocusedDay={setFocusedDay}
        />
        <div className="w-full flex justify-between items-center gap-x-3 mt-4 mb-3">
          {daysOfWeek.map((d: string, i: number) => (
            <div
              className="font-open-sans flex items-center justify-around w-full text-thryv-gray-medium-500 font-normal text-day-of-week"
              key={i}
            >
              {d}
            </div>
          ))}
        </div>
        <>
          {calendar.map((week: Moment[], i: number) => (
            <div
              key={i}
              className="w-full flex justify-start items-center gap-3 mt-2 mb-2"
            >
              {week.map((day: Moment, j: number) => {
                return (
                  <div
                    key={j}
                    onClick={() => handleDateSelection(day)}
                    className="w-full h-[25px]"
                    data-datepicker-date={`${day.format('YYYY-MM-DD')}`}
                    aria-label={`${day.format('YYYY-MM-DD')}`}
                    ref={
                      focusedDay?.isSame(day, 'day') &&
                      calendarMoment?.isSame(day, 'month')
                        ? calendarCellRef
                        : undefined
                    }
                    tabIndex={focusedDay?.isSame(day, 'day') ? 0 : -1}
                    onKeyDown={(e) => handleCalendarKeyPress(e, day)}
                    onFocus={() => setCellIsFocused(true)}
                    onBlur={() => setCellIsFocused(false)}
                  >
                    {calendarMoment && (
                      <span
                        className={`${styleDates({
                          day,
                          value: currentDate,
                          selectableDates,
                          currentMonth: calendarMoment?.format('MMMM'),
                        })} font-open-sans font-semibold text-calendar-date flex justify-around w-full h-full items-center`}
                      >
                        {day.format('D')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </>
      </div>
      {!hideFooter ? (
        <div className="w-full flex justify-end items-center py-[15px] pr-[18px] border-t border-thryv-gray-light-400">
          <div className="flex items-center gap-[23px]">
            <Button
              variant="text"
              type="button"
              level={2}
              onClick={handleCancelRegular}
              style={{ color: '#808080' }}
            >
              cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
